// sketchFillet — скругление угла между двумя линиями эскиза.
//
// Нужно для корпусов: острые углы контура скругляют ДО выдавливания, тогда
// скругление получается сквозным по всей высоте детали, а не отдельной
// операцией по каждому ребру.
//
// Как в Fusion: кликаешь по вершине, где сходятся две линии, — угол
// заменяется дугой заданного радиуса, линии укорачиваются до точек касания.

/** Расстояние между точками. */
function dist(a, b) {
    return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Посчитать скругление угла.
 *
 * corner — общая точка двух линий; a и b — их вторые концы.
 * Возвращает { t1, t2, center, ccw } или null, если скругление невозможно.
 *
 *   t1, t2 — точки касания на сторонах угла (сюда переедут концы линий);
 *   center — центр дуги;
 *   ccw    — направление обхода от t1 к t2.
 */
export function computeFillet(corner, a, b, radius) {
    const r = Math.abs(Number(radius)) || 0;
    if (r <= 0) return null;

    // Единичные векторы от вершины вдоль каждой стороны.
    const la = dist(corner, a);
    const lb = dist(corner, b);
    if (la < 1e-6 || lb < 1e-6) return null;
    const ua = { x: (a.x - corner.x) / la, y: (a.y - corner.y) / la };
    const ub = { x: (b.x - corner.x) / lb, y: (b.y - corner.y) / lb };

    // Угол между сторонами.
    const cosA = Math.max(-1, Math.min(1, ua.x * ub.x + ua.y * ub.y));
    const angle = Math.acos(cosA);
    // Стороны на одной прямой — скруглять нечего.
    if (angle < 1e-4 || Math.abs(angle - Math.PI) < 1e-4) return null;

    /*
     * Отступ от вершины до точки касания: r / tg(α/2).
     *
     * Чем острее угол, тем дальше отступ — на очень острых углах он может
     * превысить длину самой стороны, и тогда скругление не помещается.
     * Честно возвращаем null, а не строим самопересекающийся контур.
     */
    const tanHalf = Math.tan(angle / 2);
    if (Math.abs(tanHalf) < 1e-9) return null;
    const offset = r / tanHalf;
    if (offset >= la || offset >= lb) return null;

    const t1 = { x: corner.x + ua.x * offset, y: corner.y + ua.y * offset };
    const t2 = { x: corner.x + ub.x * offset, y: corner.y + ub.y * offset };

    // Центр дуги — по биссектрисе, на расстоянии r / sin(α/2).
    const bis = { x: ua.x + ub.x, y: ua.y + ub.y };
    const bl = Math.hypot(bis.x, bis.y);
    if (bl < 1e-9) return null;
    const cd = r / Math.sin(angle / 2);
    const center = {
        x: corner.x + (bis.x / bl) * cd,
        y: corner.y + (bis.y / bl) * cd,
    };

    /*
     * Направление обхода дуги от t1 к t2. Берём знак векторного произведения
     * (t1 − center) × (t2 − center): положительный — против часовой стрелки.
     */
    const c1 = { x: t1.x - center.x, y: t1.y - center.y };
    const c2 = { x: t2.x - center.x, y: t2.y - center.y };
    const cross = c1.x * c2.y - c1.y * c2.x;

    return { t1, t2, center, ccw: cross > 0 };
}

/**
 * Найти две линии, сходящиеся в точке.
 * Возвращает { lineA, lineB, otherA, otherB } или null.
 */
export function linesAtPoint(sketch, pointId) {
    const lines = (sketch.primitives || []).filter(
        (p) => p.kind === 'line' && (p.p1 === pointId || p.p2 === pointId),
    );
    // Скругление имеет смысл ровно для двух линий: одна — это не угол,
    // три и больше — непонятно, какую пару скруглять.
    if (lines.length !== 2) return null;
    const other = (l) => (l.p1 === pointId ? l.p2 : l.p1);
    return {
        lineA: lines[0],
        lineB: lines[1],
        otherA: other(lines[0]),
        otherB: other(lines[1]),
    };
}
